import { Component, HostListener, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ElementRef, ViewChild, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-page-title',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './page-title.component.html',
  styleUrls: ['./page-title.component.css']
})
export class PageTitleComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() titleKey: string = '';
  @Input() customClass: string = '';

  @ViewChild('titleContainer') titleContainer!: ElementRef;
  @ViewChild('titleText') titleText!: ElementRef;
  @ViewChild('titleHat') titleHat!: ElementRef;

  private langChangeSubscription: Subscription = new Subscription();
  private resizeObserver!: ResizeObserver; // Declare ResizeObserver
  // Removed MutationObserver to prevent recursive loops in Firefox

  constructor(private translate: TranslateService) {}

  ngAfterViewInit() {
    // Ensure fonts are loaded before positioning the hat
    document.fonts.ready.then(() => {
      this.positionHat();
    });

    // Subscribe to language changes
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      // Wait a bit for the DOM to update with new translation
      setTimeout(() => {
        document.fonts.ready.then(() => {
          this.positionHat();
        });
      }, 100);
    });

    // Initialize ResizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.positionHat());
      this.resizeObserver.observe(this.titleContainer.nativeElement);
    }

    // Removed MutationObserver initialization that was observing attributes and subtree
  }

  ngOnChanges() {
    // Reposition hat when title changes
    document.fonts.ready.then(() => {
      this.positionHat();
    });
  }

  get resolvedTitleKey(): string {
    return this.titleKey && this.titleKey.trim() !== ''
      ? this.titleKey
      : 'WELCOME_EDUCANET';
  }

  public positionHat() { // Made public
    if (!this.titleContainer || !this.titleText || !this.titleHat) return;

    const containerElement = this.titleContainer.nativeElement;
    const textElement = this.titleText.nativeElement;
    const hatElement = this.titleHat.nativeElement;

    // Get container and text dimensions
    const containerRect = containerElement.getBoundingClientRect();
    const textRect = textElement.getBoundingClientRect();
    const hatRect = hatElement.getBoundingClientRect();

    // Calculate the position of the first letter relative to the container
    const textStartX = textRect.left - containerRect.left;
    const textStartY = textRect.top - containerRect.top;

    // Position the hat at the beginning of the text span with a slight offset
    // Adjust these values as needed to fine-tune the hat's position
    hatElement.style.left = `${textStartX - (hatRect.width / 2) + 5}px`; // Centered on the first letter, adjusted by 10px
    hatElement.style.top = `${textStartY - (hatRect.height / 2) + 15}px`; // Above the first letter, adjusted by 10px
  }

  ngOnDestroy() {
    this.langChangeSubscription.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Removed MutationObserver disconnect
  }

  // Call this method if you need to reposition after window resize
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.positionHat();
  }
}